import { type ReactNode, useEffect, useState } from 'react'

import CheckIcon from '@mui/icons-material/Check'
import PauseIcon from '@mui/icons-material/Pause'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import RedoIcon from '@mui/icons-material/Redo'
import Box from '@mui/material/Box'
import Step from '@mui/material/Step'
import StepButton from '@mui/material/StepButton'
import StepContent from '@mui/material/StepContent'
import MuiStepper from '@mui/material/Stepper'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { grey } from '@mui/material/colors'

export { type Step, Stepper }

// eslint-disable-next-line no-magic-numbers
const BASE_ICON_BG_COLOR = grey[500]

type Step = {
  label: string
  caption?: string
  content: ReactNode
  status: 'required' | 'completed' | 'skipped' | 'optional' | 'paused'
}

type Props = {
  steps: Step[]
}

const Stepper = ({ steps }: Props) => {
  const [activeStep, setActiveStep] = useState(0)

  useEffect(() => {
    const activeStep = steps.findIndex((step) =>
      ['paused', 'required', 'optional'].includes(step.status),
    )

    setActiveStep(activeStep === -1 ? steps.length - 1 : activeStep)
  }, [steps])

  return (
    <MuiStepper nonLinear orientation="vertical" activeStep={activeStep}>
      {steps.map((step, index) => {
        const isCompleted = step.status === 'completed'
        const isSkipped = step.status === 'skipped'
        const isOptional = step.status === 'optional'
        const isPaused = step.status === 'paused'
        const isActive = activeStep === index

        return (
          <Step key={step.label} completed={isCompleted}>
            <StepButton
              onClick={() => setActiveStep(index)}
              icon={
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    height: 24,
                    justifyContent: 'center',
                    position: 'relative',
                    width: 24,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: isCompleted
                        ? 'success.main'
                        : isActive
                          ? 'primary.main'
                          : BASE_ICON_BG_COLOR,
                      borderRadius: '50%',
                      height: 24,
                      opacity: 0.33,
                      position: 'absolute',
                      width: 24,
                    }}
                  />

                  <Box
                    sx={{
                      alignItems: 'center',
                      backgroundColor: isCompleted
                        ? 'success.main'
                        : isActive
                          ? 'primary.main'
                          : BASE_ICON_BG_COLOR,
                      borderRadius: '50%',
                      display: 'flex',
                      height: 20,
                      justifyContent: 'center',
                      left: '50%',
                      lineHeight: '100%',
                      position: 'absolute',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 20,
                      zIndex: 1,
                    }}
                  >
                    {isCompleted ? (
                      <Tooltip title="Stap voltooid">
                        <CheckIcon
                          sx={{ color: 'common.white' }}
                          fontSize="small"
                        />
                      </Tooltip>
                    ) : isSkipped ? (
                      <Tooltip title="Stap overgeslagen">
                        <RedoIcon
                          sx={{ color: 'common.white' }}
                          fontSize="small"
                        />
                      </Tooltip>
                    ) : isPaused ? (
                      <Tooltip title="Stap gepauzeerd">
                        <PauseIcon
                          sx={{ color: 'common.white' }}
                          fontSize="small"
                        />
                      </Tooltip>
                    ) : isOptional ? (
                      <Tooltip title="Stap optioneel">
                        <QuestionMarkIcon
                          sx={{ color: 'common.white' }}
                          fontSize="small"
                        />
                      </Tooltip>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 'small',
                          color: 'common.white',
                          lineHeight: '100%',
                        }}
                      >
                        {index + 1}
                      </Typography>
                    )}
                  </Box>
                </Box>
              }
              sx={{ ...(isSkipped && { textDecoration: 'line-through' }) }}
            >
              {step.label} {isPaused && '(gepauzeerd)'}
              {step.caption && (
                <Typography
                  variant="caption"
                  sx={{ width: '100%', display: 'flex', fontSize: 'inherit' }}
                >
                  {step.caption}
                </Typography>
              )}
            </StepButton>

            <StepContent>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 2,
                }}
              >
                {step.content}
              </Box>
            </StepContent>
          </Step>
        )
      })}
    </MuiStepper>
  )
}
